import {
  absolute,
  add,
  compare,
  divide,
  fromBigInt,
  isZero,
  multiply,
  negate,
  parseDecimal,
  subtract,
  sum,
  type ExactDecimal,
} from "./decimal";
import type { Coverage } from "./coverage";
import { observedMetricEligibility } from "./coverage";

export class AnalyticsError extends Error {
  constructor(readonly code: "INVALID_FILL" | "INCOMPATIBLE_DATA", message: string) {
    super(message);
    this.name = "AnalyticsError";
  }
}

export interface BalanceInput {
  asset: string;
  trading: string;
  funding: string;
  reserved?: string;
  available?: string;
}

export function totalBalance(balance: BalanceInput): ExactDecimal {
  // reserved/available are attributes of trading, never additional buckets.
  return add(parseDecimal(balance.trading), parseDecimal(balance.funding));
}

export type FillSide = "BUY" | "SELL";
export type FeeAsset = "BASE" | "QUOTE";

export interface FillInput {
  accountScope: string;
  symbol: string;
  marketSymbol?: string;
  baseAsset: string;
  quoteAsset: string;
  matchId: string;
  orderId: string;
  side: FillSide;
  quantity: string;
  price: string;
  feeMagnitude: string;
  feeAsset: FeeAsset;
  feeIsRebate: boolean;
  timestamp: string;
  lineageId?: string;
  generation?: number;
  isMaker?: boolean;
  referralShare?: string;
}

export interface FillCalculation {
  fill: FillInput;
  notional: ExactDecimal;
  signedFee: ExactDecimal;
  dBase: ExactDecimal;
  dQuote: ExactDecimal;
}

export function calculateFill(fill: FillInput): FillCalculation {
  if (
    !fill ||
    (fill.side !== "BUY" && fill.side !== "SELL") ||
    (fill.feeAsset !== "BASE" && fill.feeAsset !== "QUOTE") ||
    typeof fill.feeIsRebate !== "boolean" ||
    !fill.accountScope ||
    !fill.baseAsset ||
    !fill.quoteAsset ||
    !fill.symbol ||
    !fill.matchId ||
    !fill.orderId
  ) {
    throw new AnalyticsError("INVALID_FILL", "Fill identity, side, assets, or fee asset is invalid.");
  }
  if (fill.side === "SELL" && fill.feeAsset === "BASE") {
    throw new AnalyticsError("INCOMPATIBLE_DATA", "A base-asset fee on a sell needs contract review; it is not silently accepted.");
  }
  const quantity = parseDecimal(fill.quantity);
  const price = parseDecimal(fill.price);
  const feeMagnitude = parseDecimal(fill.feeMagnitude);
  if (compare(quantity, fromBigInt(0n)) < 0 || compare(price, fromBigInt(0n)) < 0 || compare(feeMagnitude, fromBigInt(0n)) < 0) {
    throw new AnalyticsError("INVALID_FILL", "Quantity, price, and fee magnitude must be non-negative.");
  }
  const notional = multiply(quantity, price);
  const signedFee = fill.feeIsRebate ? feeMagnitude : negate(feeMagnitude);
  const baseFromTrade = fill.side === "BUY" ? quantity : negate(quantity);
  const quoteFromTrade = fill.side === "SELL" ? notional : negate(notional);
  const dBase = fill.feeAsset === "BASE" ? add(baseFromTrade, signedFee) : baseFromTrade;
  const dQuote = fill.feeAsset === "QUOTE" ? add(quoteFromTrade, signedFee) : quoteFromTrade;
  return { fill, notional, signedFee, dBase, dQuote };
}

function fingerprint(fill: FillInput): string {
  // IDs and financial fields are compared losslessly; decimal spelling alone
  // does not make two otherwise identical fills a conflict.
  const quantity = parseDecimal(fill.quantity);
  const price = parseDecimal(fill.price);
  const fee = parseDecimal(fill.feeMagnitude);
  return JSON.stringify([
    fill.accountScope,
    fill.symbol,
    fill.baseAsset,
    fill.quoteAsset,
    fill.matchId,
    fill.orderId,
    fill.side,
    quantity.numerator.toString(),
    quantity.denominator.toString(),
    price.numerator.toString(),
    price.denominator.toString(),
    fee.numerator.toString(),
    fee.denominator.toString(),
    fill.feeAsset,
    fill.feeIsRebate,
    fill.timestamp,
    fill.lineageId,
    fill.generation,
    fill.referralShare,
  ]);
}

export interface FillDedupResult {
  rows: FillInput[];
  duplicateRows: number;
  conflictingRows: number;
}

export function deduplicateFills(fills: readonly FillInput[]): FillDedupResult {
  const rows: FillInput[] = [];
  const seen = new Map<string, { index: number; fingerprint: string }>();
  let duplicateRows = 0;
  let conflictingRows = 0;
  for (const fill of fills) {
    const key = JSON.stringify([fill.accountScope, fill.symbol, fill.matchId, fill.orderId]);
    let currentFingerprint: string;
    try {
      currentFingerprint = fingerprint(fill);
    } catch {
      // Keep malformed rows visible for quarantine instead of throwing them
      // away during identity processing.
      currentFingerprint = `invalid:${JSON.stringify(fill)}`;
    }
    const previous = seen.get(key);
    if (!previous) {
      seen.set(key, { index: rows.length, fingerprint: currentFingerprint });
      rows.push(fill);
      continue;
    }
    if (previous.fingerprint === currentFingerprint) {
      duplicateRows += 1;
    } else {
      // Retain the first row for evidence/display, but block affected metrics.
      conflictingRows += 1;
    }
  }
  return { rows, duplicateRows, conflictingRows };
}

export interface AssetValue {
  asset: string;
  value: ExactDecimal;
}

export interface FeeAssetSummary {
  asset: string;
  grossCharges: ExactDecimal;
  grossRebates: ExactDecimal;
  netFees: ExactDecimal;
}

export interface ObservedFillSummary {
  calculations: FillCalculation[];
  quarantined: FillInput[];
  volumeByQuote: AssetValue[];
  baseFlowByAsset: AssetValue[];
  observedNetQuoteFlowByQuote: AssetValue[];
  feesByAsset: FeeAssetSummary[];
  duplicateRows: number;
  conflictingRows: number;
  unknownRows: number;
  droppedRows: 0;
  rowsReceived: number;
  rowsAccepted: number;
  metricLabel: "observed-covered-rows" | "observed-in-loaded-rows" | "observed-metrics-blocked";
  eligible: boolean;
  issues: string[];
}

function addToMap(map: Map<string, ExactDecimal>, asset: string, value: ExactDecimal): void {
  map.set(asset, add(map.get(asset) ?? fromBigInt(0n), value));
}

function mapValues(map: Map<string, ExactDecimal>): AssetValue[] {
  return [...map.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([asset, value]) => ({ asset, value }));
}

export function summarizeFills(
  fills: readonly FillInput[],
  coverage?: Pick<Coverage, "status">,
): ObservedFillSummary {
  const deduped = deduplicateFills(fills);
  const calculations: FillCalculation[] = [];
  const quarantined: FillInput[] = [];
  const issues: string[] = [];
  const volume = new Map<string, ExactDecimal>();
  const baseFlow = new Map<string, ExactDecimal>();
  const quoteFlow = new Map<string, ExactDecimal>();
  const fees = new Map<string, { charges: ExactDecimal; rebates: ExactDecimal }>();

  for (const fill of deduped.rows) {
    try {
      const calculation = calculateFill(fill);
      calculations.push(calculation);
      addToMap(volume, fill.quoteAsset, calculation.notional);
      addToMap(baseFlow, fill.baseAsset, calculation.dBase);
      addToMap(quoteFlow, fill.quoteAsset, calculation.dQuote);
      const feeGroup = fees.get(fill.feeAsset === "BASE" ? fill.baseAsset : fill.quoteAsset) ?? {
        charges: fromBigInt(0n),
        rebates: fromBigInt(0n),
      };
      if (fill.feeIsRebate) feeGroup.rebates = add(feeGroup.rebates, parseDecimal(fill.feeMagnitude));
      else feeGroup.charges = add(feeGroup.charges, parseDecimal(fill.feeMagnitude));
      fees.set(fill.feeAsset === "BASE" ? fill.baseAsset : fill.quoteAsset, feeGroup);
    } catch (error) {
      quarantined.push(fill);
      issues.push(error instanceof Error ? error.message : "Invalid fill row.");
    }
  }
  if (deduped.conflictingRows > 0) issues.push("Conflicting duplicate fills block affected aggregates.");
  const unknownRows = quarantined.length;
  const eligible = deduped.conflictingRows === 0 && unknownRows === 0;
  const status = coverage?.status;
  const metricLabel = !eligible
    ? "observed-metrics-blocked"
    : status === "partial" || status === "stale" || status === "loading"
      ? "observed-in-loaded-rows"
      : "observed-covered-rows";
  return {
    calculations,
    quarantined,
    volumeByQuote: mapValues(volume),
    baseFlowByAsset: mapValues(baseFlow),
    observedNetQuoteFlowByQuote: mapValues(quoteFlow),
    feesByAsset: [...fees.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([asset, group]) => ({
        asset,
        grossCharges: group.charges,
        grossRebates: group.rebates,
        netFees: subtract(group.charges, group.rebates),
      })),
    duplicateRows: deduped.duplicateRows,
    conflictingRows: deduped.conflictingRows,
    unknownRows,
    droppedRows: 0,
    rowsReceived: fills.length,
    rowsAccepted: calculations.length,
    metricLabel,
    eligible,
    issues,
  };
}

export function observedFillEligibility(
  summary: ObservedFillSummary,
  coverage: Coverage,
): { available: boolean; reason?: string } {
  if (!summary.eligible) return { available: false, reason: "Fill conflicts or unknown rows block this aggregate." };
  // Transport coverage and the normalized summary count different subsets of
  // rows. Neither counter set may erase the other: doing so can make partial
  // coverage look clean or hide a duplicate/conflict detected upstream.
  const rowsAccepted = summary.rowsAccepted;
  const duplicateRows = Math.max(coverage.duplicateRows, summary.duplicateRows);
  const conflictingRows = Math.max(coverage.conflictingRows, summary.conflictingRows);
  const unknownRows = Math.max(coverage.unknownRows, summary.unknownRows);
  const droppedRows = Math.max(coverage.droppedRows, summary.droppedRows);
  const rowsReceived = Math.max(
    coverage.rowsReceived,
    summary.rowsReceived,
    rowsAccepted + duplicateRows + conflictingRows + unknownRows + droppedRows,
  );
  return observedMetricEligibility({
    ...coverage,
    rowsReceived,
    rowsAccepted,
    duplicateRows,
    conflictingRows,
    unknownRows,
    droppedRows,
  });
}

export interface SampledEquityMetrics {
  /** No point timestamps/order contract is available; these are order-agnostic values only. */
  label: "sampled-valuation-order-unverified-not-pnl";
  sampleCount: number;
  observedLow?: ExactDecimal;
  observedHigh?: ExactDecimal;
}

/**
 * Summarize sampled equity without treating array position as time. The
 * upstream response has range bounds but no per-point timestamps, and its
 * ordering contract is not verified. Therefore latest/change/drawdown are not
 * computed here.
 */
export function sampledEquityMetrics(equities: readonly string[]): SampledEquityMetrics {
  if (equities.length === 0) {
    return { label: "sampled-valuation-order-unverified-not-pnl", sampleCount: 0 };
  }
  const values = equities.map(parseDecimal);
  let observedLow = values[0];
  let observedHigh = values[0];
  for (const value of values.slice(1)) {
    if (compare(value, observedLow) < 0) observedLow = value;
    if (compare(value, observedHigh) > 0) observedHigh = value;
  }
  return {
    label: "sampled-valuation-order-unverified-not-pnl",
    sampleCount: values.length,
    observedLow,
    observedHigh,
  };
}

export function currentRateScenario(
  notional: ExactDecimal,
  makerPercent: ExactDecimal,
  takerPercent: ExactDecimal,
): { makerEstimate: ExactDecimal; takerEstimate: ExactDecimal; label: "current-rate-scenario-not-historical-fee" } {
  return {
    makerEstimate: divide(multiply(notional, makerPercent), fromBigInt(100n)),
    takerEstimate: divide(multiply(notional, takerPercent), fromBigInt(100n)),
    label: "current-rate-scenario-not-historical-fee",
  };
}

export function sumExact(values: readonly ExactDecimal[]): ExactDecimal {
  return sum(values);
}

export function isExactZero(value: ExactDecimal): boolean {
  return isZero(value);
}

export function absoluteExact(value: ExactDecimal): ExactDecimal {
  return absolute(value);
}
